import {
	PatchEndPoint,
	GetEndPoint,
	PostEndPoint,
	CompanyUsersEndPoint,
	FormFieldsEndPoint,
	GetTransporterTrucksData,
	GetAllEndPoint,
	GetUserCompaniesData,
	GetAccessDataEndPoint,
	GetAuthzCompaniesEndPoint,
	GetCompanyRoleCatalogEndPoint,
	GetAuthzPublicMenuEndPoint,
	GetCompanyWorkflowsEndPoint,
	AddUserData,
	UnassignUserData,
	GetTransporterGeneratorsData,
	AddTransporterGeneratorData,
	EditTransporterGeneratorsData,
	DeleteTransporterGeneratorData,
	GetTransporterContactsData,
	AddTransporterContactData,
	EditTransporterContactData,
	DeleteTransporterContactData,
	AddTransporterTruckData,
	DeleteTransporterTruckData,
	EditTransporterTruckData,
	GetAllTemplatesEndPoint,
	AddTemplateEndPoint,
	DeleteTemplateEndPoint,
	DownloadTemplateEndPoint,
	GetBranchesCompanyEndPoint,
	GetCRDetails,
	GetDomainDataEndPoint,
	GetLabelsData,
	GetServicesData,
	GetUserManualsData,
	ImportCustomDataEndPoint,
	DeleteDBEndPoint,
} from "common/constants";
import { makeAuthenticatedRequest } from "../helpers/authenticatedRequest";
import { makeRequest } from "../helpers/makeRequest";

// #region PUBLIC

// authz.* schema: the anonymous ("public") menu (About Us, Contact Us, ...) for the application
// the domain points to - genuinely unauthenticated, no token sent.
export const getAuthzPublicMenu = async (domainId, signal = null) => {
	const response = await makeRequest(`${GetAuthzPublicMenuEndPoint}/${encodeURIComponent(domainId)}`, {
		method: "GET",
		signal,
	});

	return response;
};

export const getDomainData = async (domainName, signal = null) => {
	const url = `${GetDomainDataEndPoint}/${domainName}`;

	const response = await makeRequest(url, {
		method: "GET",
		signal,
	});

	return response;
}

export const getLabelsData = async (language, signal = null) => {
	const url = `${GetLabelsData}/${language}`;

	const response = await makeRequest(url, {
		method: "GET",
		signal,
	});

	return response;
}

export const getServicesData = async (domainId, pageName, signal = null) => {
	const url = `${GetServicesData}/${domainId}/${pageName}`;

	const response = await makeRequest(url, {
		method: "GET",
		signal,
	});

	return response;
}

export const getUserManualsData = async (signal = null) => {
	const response = await makeRequest(GetUserManualsData, {
		method: "GET",
		signal,
	});

	return response;
}
// #endregion

// #region TABLE COLUMN CONFIGS

export const getTableColumnConfigs = async (domainId, tableName, signal = null) => {
	const filter = `domain_id=eq.${domainId}&table_name=eq.${tableName}&is_active=eq.true`;
	const url = `${GetAllEndPoint}?TableName=tbl_table_column_configs&Filter=${encodeURIComponent(filter)}`;
	const response = await makeAuthenticatedRequest(url, { method: "GET", signal });
	return response;
};

// #endregion

// #region IMPORT DATA TYPE CONFIGS

export const getImportDataTypeConfigs = async (domainId, signal = null) => {
	const filter = `domain_id=eq.${domainId}&is_active=eq.true`;
	const url = `${GetAllEndPoint}?TableName=tbl_import_data_type_configs&Filter=${encodeURIComponent(filter)}`;
	const response = await makeAuthenticatedRequest(url, { method: "GET", signal });
	return response;
};

// #endregion

// #region DATABASE

export const patchDatabase = async (JsonToPatch, signal = null) => {

	const response = await makeAuthenticatedRequest(PatchEndPoint, {
		method: "PATCH",
		body: JSON.stringify(JsonToPatch),
		signal,
	});

	return response;
};

export const postDatabaseRecord = async (jsonBody, signal = null) => {
	const response = await makeAuthenticatedRequest(PostEndPoint, {
		method: "POST",
		body: JSON.stringify(jsonBody),
		signal,
	});
	return response;
};

export const deleteDatabaseRecord = async (jsonBody, signal = null) => {
	const response = await makeAuthenticatedRequest(DeleteDBEndPoint, {
		method: "DELETE",
		body: JSON.stringify(jsonBody),
		signal,
	});
	return response;
};

export const getDatabaseRecord = async ({ tableName, fieldName, itemId, columns }, signal = null) => {
	const params = [`TableName=${tableName}`];
	if (fieldName) params.push(`FieldName=${fieldName}`);
	if (itemId) params.push(`FieldValue=${itemId}`);
	if (columns) params.push(`Columns=${columns}`);

	const url = `${GetEndPoint}?${params.join("&")}`;

	const response = await makeAuthenticatedRequest(url, { method: "GET", signal, });

	return response;
};

export const getAllDatabaseRecord = async ({ tableName, condition, columns }, signal) => {
	const params = [`TableName=${tableName}`];
	if (condition) params.push(`Filter=${encodeURIComponent(condition)}`);
	if (columns) params.push(`Columns=${columns}`);

	const url = `${GetAllEndPoint}?${params.join("&")}`;

	const response = await makeAuthenticatedRequest(url, {
		method: "GET",
		signal,
	});

	return response;
};

// #endregion

// #region WORKFLOWS
export const getWorkflowByID = async ({ applicationID, createdId }, signal = null) => {
	const url = `${GetAllEndPoint}?TableName=${applicationID}&Filter=id=eq.${createdId}`
	const response = await makeAuthenticatedRequest(url, { signal });
	return response;
};

// #endregion

// #region USERS + ROLES + COMPANIES
// authz.* schema resolution: roles/permissions/menu for the caller (identity read from the
// token) in one company, scoped to the application the token was issued for.
export const getAccessData = async (companyId, signal = null) => {
	const url = `${GetAccessDataEndPoint}/${companyId}`;
	const response = await makeAuthenticatedRequest(url, { signal });
	return response;
};

// authz.* schema: the workflows the caller can see in one company, each with the permission
// codes usable on it. Fetched on demand by the workflow pages - see useWorkflowAccess.
export const getCompanyWorkflows = async (companyId, signal = null) => {
	const url = `${GetCompanyWorkflowsEndPoint}/${companyId}/workflows`;
	const response = await makeAuthenticatedRequest(url, { signal });
	return response;
};

export const getUserCompanies = async (userId, signal = null) => {
	const url = `${GetUserCompaniesData}/${userId}`;
	const response = await makeAuthenticatedRequest(url, { signal });
	return response;
};

// authz.* schema: companies the caller (identity read from the token) belongs to, filtered to
// those with the current application enabled. Replaces getUserCompanies for the company picker.
export const getAuthzCompanies = async (signal = null) => {
	const response = await makeAuthenticatedRequest(GetAuthzCompaniesEndPoint, { signal });
	return response;
};

// The live members of a company (authz), with their details and roles in it. The backend builds the
// query itself from the company id and checks the caller holds manage_users there.
export const getCompanyUsers = async (companyId, signal = null) => {
	const url = `${CompanyUsersEndPoint}/${encodeURIComponent(companyId)}/users`;
	return await makeAuthenticatedRequest(url, { method: "GET", signal });
};

// One member of a company, in the same shape as the list. Your own record needs only membership of the
// company (My Profile); anyone else's needs manage_users.
export const getCompanyUser = async (companyId, userId, signal = null) => {
	const url = `${CompanyUsersEndPoint}/${encodeURIComponent(companyId)}/users/${encodeURIComponent(userId)}`;
	return await makeAuthenticatedRequest(url, { method: "GET", signal });
};

// The roles the caller may give or take away in a company (authz_role_assignable_roles): the role
// picker of Add user / Edit roles.
export const getAssignableRoles = async (companyId, signal = null) => {
	const url = `${CompanyUsersEndPoint}/${encodeURIComponent(companyId)}/assignable-roles`;
	return await makeAuthenticatedRequest(url, { method: "GET", signal });
};

// Adds a person to a company. body = { fields: { <field_key>: value }, roleIds: [...] }; the backend
// validates the fields against the application's "AddUser" form config.
export const addCompanyUser = async (companyId, body) => {
	const url = `${CompanyUsersEndPoint}/${encodeURIComponent(companyId)}/users`;
	return await makeAuthenticatedRequest(url, { method: "POST", body: JSON.stringify(body) });
};

// Edits a member of a company. body = { fields: { <field_key>: value } } with only the changed fields;
// the backend validates them against the application's "EditUser" form config.
export const editCompanyUser = async (companyId, userId, body) => {
	const url = `${CompanyUsersEndPoint}/${encodeURIComponent(companyId)}/users/${encodeURIComponent(userId)}`;
	return await makeAuthenticatedRequest(url, { method: "PATCH", body: JSON.stringify(body) });
};

// Sets a member's roles in a company. body = { roleIds: [...] }: the complete new set (empty = no roles).
export const updateCompanyUserRoles = async (companyId, userId, body) => {
	const url = `${CompanyUsersEndPoint}/${encodeURIComponent(companyId)}/users/${encodeURIComponent(userId)}/roles`;
	return await makeAuthenticatedRequest(url, { method: "PUT", body: JSON.stringify(body) });
};

// Removes a member from a company - not from the system: their membership and roles in this company end;
// their account and other companies stay.
export const removeCompanyUser = async (companyId, userId) => {
	const url = `${CompanyUsersEndPoint}/${encodeURIComponent(companyId)}/users/${encodeURIComponent(userId)}`;
	return await makeAuthenticatedRequest(url, { method: "DELETE" });
};

// The fields of an app form (e.g. "AddUser") for the application of the caller's token, in display order.
export const getFormFields = async (formKey, signal = null) => {
	const url = `${FormFieldsEndPoint}/${encodeURIComponent(formKey)}`;
	return await makeAuthenticatedRequest(url, { method: "GET", signal });
};

// authz.* schema: every role assignable in this company (private to it, or global to the
// realm/application), independent of which roles the caller themselves holds. Replaces the old
// GetCompanyRolesData (hierarchical isAdmin/isSuperAdmin/parentRoleId model, now retired) -
// response shape is a clean {roleId, roleCode, displayName} list, see CompanyRoleDTO.
export const getCompanyRoles = async (companyId, signal) => {
	const url = `${GetCompanyRoleCatalogEndPoint}/${companyId}/roles`;
	const response = await makeAuthenticatedRequest(url, {
		method: "GET",
		signal,
	});
	return response;
};

export const addUser = async (companyId, keycloakCompanyId, bodyData) => {
	const url = `${AddUserData}/${companyId}/keycloak/${keycloakCompanyId}/admin/false`;
	const response = await makeAuthenticatedRequest(url, {
		method: "POST",
		body: JSON.stringify(bodyData),
	});
	return response;
};

export const unassignUser = async (userId, companyId, keycloakCompanyId) => {
	const url = `${UnassignUserData}/${userId}/company/${companyId}/keycloak/${keycloakCompanyId}`;
	const response = await makeAuthenticatedRequest(url, {
		method: "DELETE",
	});
	console.log("asd")
	return response;
};

// #endregion

// #region GENERATORS

export const getTransporterGeneratorsData = async (companyId, signal = null) => {
	const url = `${GetTransporterGeneratorsData}/${companyId}`;
	const response = await makeAuthenticatedRequest(url, { method: "GET", signal });
	return response;
};

export const addTransporterGenerator = async (companyId, data) => {
	const url = `${AddTransporterGeneratorData}/${companyId}`;
	const response = await makeAuthenticatedRequest(url, { method: "POST", body: JSON.stringify(data) });
	return response;
};

export const editTransporterGenerator = async (generatorId, data) => {
	const url = `${EditTransporterGeneratorsData}/${generatorId}`;
	const response = await makeAuthenticatedRequest(url, { method: "PUT", body: JSON.stringify(data) });

	return response;
};

export const deleteTransporterGenerator = async (generatorId) => {
	const url = `${DeleteTransporterGeneratorData}/${generatorId}`;
	const response = await makeAuthenticatedRequest(url, { method: "DELETE" });
	return response;
};

// #endregion

// #region CONTACTS/DELEGATES

export const getTransporterGeneratorContactsData = async (companyId, signal = null) => {
	const url = `${GetTransporterContactsData}/${companyId}`;
	const response = await makeAuthenticatedRequest(url, { method: "GET", signal });
	return response;
};

export const addTransporterGeneratorContact = async (companyId, data) => {
	const url = `${AddTransporterContactData}/${companyId}`;
	const response = await makeAuthenticatedRequest(url, { method: "POST", body: JSON.stringify(data) });
	return response;
};

export const editTransporterGeneratorContact = async (contactId, data) => {
	const url = `${EditTransporterContactData}/${contactId}`;
	const response = await makeAuthenticatedRequest(url, { method: "PUT", body: JSON.stringify(data) });
	return response;
};

export const deleteTransporterGeneratorContact = async (contactId) => {
	const url = `${DeleteTransporterContactData}/${contactId}`;
	const response = await makeAuthenticatedRequest(url, { method: "DELETE" });
	return response;
};

// #endregion

// #region FLEET

export const getTrucksData = async (companyId, signal = null) => {
	const url = `${GetTransporterTrucksData}/${companyId}`;
	const response = await makeAuthenticatedRequest(url, { signal });
	return response;
};

export const addTransporterTruck = async (relatedCompanyId, data) => {
	const url = `${AddTransporterTruckData}/${relatedCompanyId}`;
	const response = await makeAuthenticatedRequest(url, { method: "POST", body: JSON.stringify(data) });
	return response;
};

export const editTransporterTruck = async (truckId, data) => {
	const url = `${EditTransporterTruckData}/${truckId}`;
	const response = await makeAuthenticatedRequest(url, { method: "PUT", body: JSON.stringify(data) });
	return response;
};

export const deleteTransporterTruck = async (truckId) => {
	const url = `${DeleteTransporterTruckData}/${truckId}`;
	const response = await makeAuthenticatedRequest(url, { method: "DELETE" });
	return response;
};

// #endregion


// #region MOC
export const getCRDetails = async (unn, signal = null) => {
	const url = `${GetCRDetails}?crNationalNumber=${unn}`;
	const response = await makeAuthenticatedRequest(url, {
		method: "GET",
		signal,
	});
	return response;
};
// #endregion


// #region TEMPLATES
export const getTemplatesData = async (signal = null) => {
	const response = await makeAuthenticatedRequest(GetAllTemplatesEndPoint, {
		method: "GET",
		signal,
	});
	return response;
};

// Multipart upload: the name and the file travel as form parts (nothing in the URL, no base64).
export const addTemplate = async (templateName, file) => {
	const body = new FormData();
	body.append("templateName", templateName);
	body.append("file", file, file.name);
	const response = await makeAuthenticatedRequest(AddTemplateEndPoint, {
		method: "POST",
		body,
	});
	return response;
};

// templateId below is always a version's Carbone id (a template row's templateId), never a name.

// What deleting this version (or its whole template) would affect: { isInUse, versionCount, documentCount, linkedSteps, ... }.
export const getTemplateUsage = async (templateId) => {
	return await makeAuthenticatedRequest(`${DeleteTemplateEndPoint}/${encodeURIComponent(templateId)}/usage`, { method: "GET" });
};

// Makes this version the one workflows use from now on.
export const setTemplateVersionInUse = async (templateId) => {
	return await makeAuthenticatedRequest(`${DeleteTemplateEndPoint}/${encodeURIComponent(templateId)}/use`, { method: "POST" });
};

// Deletes one version (refused for the version in use).
export const deleteTemplateVersion = async (templateId) => {
	return await makeAuthenticatedRequest(`${DeleteTemplateEndPoint}/versions/${encodeURIComponent(templateId)}`, { method: "DELETE" });
};

// Deletes the whole template this version belongs to (refused while workflow steps are linked to it).
export const deleteTemplate = async (templateId) => {
	return await makeAuthenticatedRequest(`${DeleteTemplateEndPoint}/${encodeURIComponent(templateId)}`, { method: "DELETE" });
};

// The file itself: returns { blob, fileName, contentType }.
export const downloadTemplate = async (templateId) => {
	const url = `${DownloadTemplateEndPoint}/${encodeURIComponent(templateId)}`;
	return await makeAuthenticatedRequest(url, {
		method: "GET",
		responseType: "blob",
	});
};
// #endregion

// #region BRANCHES
export const getCompanyBranches = async (companyId, signal = null) => {
	const url = `${GetBranchesCompanyEndPoint}/${companyId}`;
	const response = await makeAuthenticatedRequest(url, { method: "GET", signal });

	return response;
};
// #endregion

// #region Import Data
export const importCustomData = async (tableKey, bodyData) => {
	const response = await makeAuthenticatedRequest(ImportCustomDataEndPoint, {
		method: "POST",
		body: JSON.stringify({ tableKey, bodyData }),
	});
	return response;
};
// #endregion